import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { i18nLanguage } from "i18n";
import type { StopGroupMedia } from "../../types";
import { getCloudinaryImageUrl } from "../../utils/getCloudinaryImageUrl";
import { getLocalizedString } from "../../utils/getLocalizedString";
import { WidgetVideoPlayer } from "./WidgetVideoPlayer";

type GroupMediaCarouselProps = {
  media: StopGroupMedia;
  language: i18nLanguage;
};

function useAccentColor(ref: React.RefObject<HTMLElement | null>): string | undefined {
  const [color, setColor] = useState<string>();
  useEffect(() => {
    if (!ref.current) return;
    const val = getComputedStyle(ref.current).getPropertyValue("--tripper-primary").trim();
    if (val) setColor(val);
  }, [ref]);
  return color;
}

function resolveImageUrl(
  raw: string | null | undefined,
  transformation: string,
): string | null {
  if (!raw?.trim()) return null;
  const s = raw.trim();
  if (/^https?:\/\//i.test(s)) return s;
  return getCloudinaryImageUrl(s, transformation);
}

export const GroupMediaCarousel = ({
  media,
  language,
}: GroupMediaCarouselProps) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const slides = useMemo(() => {
    if (media.type === "gallery") {
      const ids = (media.images ?? []).filter(Boolean) as string[];
      return ids
        .map((id) => {
          const thumb =
            resolveImageUrl(id, "f_auto,w_480,c_limit,q_auto") ?? "";
          const full =
            resolveImageUrl(id, "f_auto,w_1600,c_limit,q_auto") ?? thumb;
          return thumb ? { thumb, full } : null;
        })
        .filter(Boolean) as { thumb: string; full: string }[];
    }
    if (media.type === "image") {
      const primary = media.mediaId ?? media.src;
      const thumb =
        resolveImageUrl(primary, "f_auto,w_480,c_limit,q_auto") ?? "";
      const full =
        resolveImageUrl(primary, "f_auto,w_1600,c_limit,q_auto") ?? thumb;
      if (!thumb) return [];
      return [{ thumb, full }];
    }
    return [];
  }, [media]);

  const altText = media.alt ? getLocalizedString(media.alt, language) : "";

  useEffect(() => {
    if (lightboxIndex == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxIndex(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightboxIndex]);

  const videoWrapRef = useRef<HTMLDivElement>(null);
  const accentColor = useAccentColor(videoWrapRef);

  if (media.type === "mux") {
    const playbackId = media.mediaId;
    if (!playbackId) return null;
    return (
      <div ref={videoWrapRef} className="tripper-group__media tripper-group__media--video">
        <WidgetVideoPlayer playbackId={playbackId} accentColor={accentColor} />
      </div>
    );
  }

  if (slides.length === 0) return null;

  const hasLightboxNav = slides.length > 1;
  const activeSlide =
    lightboxIndex != null ? slides[lightboxIndex] ?? null : null;
  const gridCount = slides.length >= 3 ? 3 : slides.length;
  const usesOverflowCard = slides.length >= 4;
  const previewSlides = usesOverflowCard ? slides.slice(0, 2) : slides.slice(0, gridCount);

  return (
    <>
      <div
        className={`tripper-group-gallery-grid${gridCount === 1 ? " tripper-group-gallery-grid--single" : ""}${gridCount === 2 ? " tripper-group-gallery-grid--two" : ""}`}
        style={
          {
            "--tripper-gallery-columns": String(gridCount),
          } as CSSProperties
        }
      >
        {previewSlides.map((slide, i) => (
          <button
            key={`${slide.thumb}-${i}`}
            type="button"
            className="tripper-group-gallery-grid__item"
            aria-label={
              altText
                ? `${altText} (${i + 1} of ${slides.length})`
                : `Image ${i + 1} of ${slides.length}`
            }
            onClick={() => setLightboxIndex(i)}
          >
            <img
              src={slide.thumb}
              alt={altText || `Group image ${i + 1}`}
              className="tripper-group-gallery-grid__img"
              draggable={false}
            />
          </button>
        ))}
        {usesOverflowCard && (
          <button
            type="button"
            className="tripper-group-gallery-grid__more"
            aria-label={`View all ${slides.length} photos`}
            onClick={() => setLightboxIndex(0)}
          >
            <span className="tripper-group-gallery-grid__more-text">
              View all {slides.length} photos
            </span>
            <span className="tripper-group-gallery-grid__more-icon" aria-hidden>
              <ArrowRightIcon />
            </span>
          </button>
        )}
      </div>

      {activeSlide && lightboxIndex != null && (
        <div
          className="tripper-lightbox"
          onClick={() => setLightboxIndex(null)}
          role="presentation"
        >
          <button
            type="button"
            className="tripper-lightbox__close"
            aria-label="Close"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex(null);
            }}
          >
            <CloseIcon />
          </button>
          <div
            className="tripper-lightbox__content"
            onClick={(e) => e.stopPropagation()}
          >
            {hasLightboxNav && lightboxIndex > 0 && (
              <button
                type="button"
                className="tripper-lightbox__nav tripper-lightbox__nav--left"
                aria-label="Previous image"
                onClick={() => setLightboxIndex((i) => (i != null ? i - 1 : i))}
              >
                <ChevronIcon dir="left" large />
              </button>
            )}
            <img
              src={activeSlide.full}
              alt={altText || `Group image ${lightboxIndex + 1}`}
              className="tripper-lightbox__img"
            />
            {hasLightboxNav && lightboxIndex < slides.length - 1 && (
              <button
                type="button"
                className="tripper-lightbox__nav tripper-lightbox__nav--right"
                aria-label="Next image"
                onClick={() => setLightboxIndex((i) => (i != null ? i + 1 : i))}
              >
                <ChevronIcon dir="right" large />
              </button>
            )}
          </div>
          {hasLightboxNav && (
            <span className="tripper-lightbox__counter">
              {lightboxIndex + 1} / {slides.length}
            </span>
          )}
        </div>
      )}
    </>
  );
};

const ChevronIcon = ({
  dir,
  large,
}: {
  dir: "left" | "right";
  large?: boolean;
}) => {
  const size = large ? 22 : 16;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {dir === "left" ? (
        <path d="M15 6l-6 6 6 6" />
      ) : (
        <path d="M9 6l6 6-6 6" />
      )}
    </svg>
  );
};

const CloseIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);
